import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, KeyRound, Archive, Printer, SlidersHorizontal } from "lucide-react";
import { staffApi, apiError } from "../lib/api";

const STATE_STYLE = {
  "Not Started": "bg-[#F3EFE0] text-[#566270]",
  Learning: "bg-[#F6EED8] text-[#8a6a10]",
  Practising: "bg-[#F6EED8] text-[#8a6a10]",
  Secure: "bg-[#EBF6F0] text-[#2D6A4F]",
  Mastered: "bg-[#FBF0F2] text-[#7A1C2E]",
};

export default function ChoristerDetail() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [pinResult, setPinResult] = useState(null);
  const [error, setError] = useState("");
  const [grade, setGrade] = useState(null);
  const [diag, setDiag] = useState(null);

  const load = useCallback(() => staffApi.get(`/staff/choristers/${id}`).then((r) => {
    setD(r.data);
    setGrade(r.data.chorister.grade);
  }), [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    staffApi.get(`/staff/choristers/${id}/diagnostics`).then((r) => setDiag(r.data)).catch(() => {});
  }, [id]);

  const resetPin = async () => {
    try {
      const { data } = await staffApi.post(`/staff/choristers/${id}/reset-pin`);
      setPinResult(data);
    } catch (e) { setError(apiError(e)); }
  };

  const changeGrade = async (g) => {
    setGrade(g);
    await staffApi.post(`/staff/choristers/${id}/grade`, { grade: g });
    load();
  };

  const setStatus = async (status) => {
    await staffApi.post(`/staff/choristers/${id}/status`, { status });
    load();
  };

  const toggleModule = async (mod) => {
    const overrides = d.chorister && d.module_access
      .filter((m) => m.override !== null && m.override !== undefined)
      .map((m) => ({ module_id: m.id, enabled: m.override }));
    const existing = overrides.find((o) => o.module_id === mod.id);
    if (existing) existing.enabled = !mod.enabled;
    else overrides.push({ module_id: mod.id, enabled: !mod.enabled });
    await staffApi.put(`/staff/choristers/${id}/modules`, { overrides });
    load();
  };

  if (!d) return <p className="text-[#566270]" data-testid="detail-loading">Loading…</p>;
  const c = d.chorister;

  return (
    <div className="space-y-6" data-testid="chorister-detail">
      <Link to="/staff/choristers" className="inline-flex items-center gap-2 text-sm font-bold text-[#566270] hover:text-[#0B192C]" data-testid="back-to-list">
        <ArrowLeft className="w-4 h-4" /> All choristers
      </Link>

      <div className="rounded-2xl bg-[#0B192C] text-[#FAF7EE] p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl font-bold" data-testid="chorister-name">{c.display_name}</h1>
            <p className="text-sm text-[#FAF7EE]/60 font-note">{c.username} · {c.groups.join(", ") || "No group"} · {c.status}</p>
            <div className="flex flex-wrap gap-2 mt-3">
              <span className="px-3 py-1.5 rounded-full bg-[#C69B3D] text-[#0B192C] text-xs font-bold" data-testid="detail-notes">{c.notes_total.toLocaleString()} Notes</span>
              <span className="px-3 py-1.5 rounded-full bg-[#12253E] text-xs font-bold">{c.streak}-day streak</span>
              <span className="px-3 py-1.5 rounded-full bg-[#12253E] text-xs font-bold">Mastery {c.mastery_avg}%</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs uppercase tracking-widest text-[#FAF7EE]/60" htmlFor="grade-select">Grade</label>
            <select id="grade-select" value={grade} onChange={(e) => changeGrade(Number(e.target.value))} data-testid="grade-select"
              className="rounded-xl bg-[#12253E] border border-[#1E3A5F] px-3 py-2.5 text-sm font-bold min-h-[48px]">
              {[1, 2, 3, 4, 5].map((g) => <option key={g} value={g}>Grade {g}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <button onClick={resetPin} data-testid="reset-pin-btn"
          className="inline-flex items-center gap-2 px-4 py-3 rounded-xl bg-[#C69B3D] text-[#0B192C] font-bold text-sm min-h-[48px]">
          <KeyRound className="w-4 h-4" /> Reset PIN
        </button>
        <Link to="/staff/login-cards" data-testid="print-card-btn"
          className="inline-flex items-center gap-2 px-4 py-3 rounded-xl border-2 border-[#0B192C] text-[#0B192C] font-bold text-sm min-h-[48px]">
          <Printer className="w-4 h-4" /> Print login card
        </Link>
        {c.status === "active" ? (
          <button onClick={() => setStatus("archived")} data-testid="archive-btn"
            className="inline-flex items-center gap-2 px-4 py-3 rounded-xl border-2 border-[#7A1C2E] text-[#7A1C2E] font-bold text-sm min-h-[48px]">
            <Archive className="w-4 h-4" /> Archive profile
          </button>
        ) : (
          <button onClick={() => setStatus("active")} data-testid="reactivate-btn"
            className="inline-flex items-center gap-2 px-4 py-3 rounded-xl bg-[#2D6A4F] text-white font-bold text-sm min-h-[48px]">
            Reactivate
          </button>
        )}
      </div>

      {pinResult && (
        <div className="rounded-2xl bg-[#EBF6F0] border-2 border-[#2D6A4F] p-5" data-testid="pin-result">
          <p className="text-xs font-bold uppercase tracking-widest text-[#2D6A4F]">New PIN — shown once only</p>
          <p className="font-note text-2xl font-bold text-[#0B192C] mt-1">{pinResult.username} · {pinResult.pin}</p>
        </div>
      )}
      {error && <p role="alert" className="text-sm text-[#A62B2B]">{error}</p>}

      <div className="grid gap-6 lg:grid-cols-2">
        <section className="rounded-2xl bg-white border border-[#E5DEC9] p-6" data-testid="baseline-section">
          <h2 className="font-display text-lg font-bold text-[#0B192C]">Starting-point profile</h2>
          {!diag?.baseline ? (
            <p className="text-sm text-[#566270] mt-3" data-testid="baseline-empty">
              No baseline yet — this chorister can run "Find My Starting Point" from their home screen.
            </p>
          ) : (
            <>
              <p className="text-xs text-[#566270] mt-1">
                Baseline from {diag.baseline.completed_at ? new Date(diag.baseline.completed_at).toLocaleDateString("en-GB") : "—"}
                {" "}· preserved permanently. Suggestions only — you stay in control of unlocking.
              </p>
              <div className="mt-3 space-y-2">
                {(diag.areas || []).map((a) => {
                  const s = diag.baseline.suggestions?.[a.key];
                  const latest = diag.progress_checks?.length
                    ? diag.progress_checks[diag.progress_checks.length - 1].suggestions?.[a.key] : null;
                  const askedCount = diag.baseline.areas?.[a.key]?.asked?.length ?? null;
                  if (!s) return null;
                  const notAssessed = s.level === null || askedCount === 0;
                  return (
                    <div key={a.key} className="rounded-xl border border-[#F3EFE0] px-4 py-3" data-testid={`baseline-area-${a.key}`}>
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-sm font-semibold text-[#0B192C]">{a.label}</span>
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${notAssessed ? "bg-[#F3EFE0] text-[#566270]" : "bg-[#F6EED8] text-[#8a6a10]"}`}
                          data-testid={`baseline-level-${a.key}`}>{notAssessed ? "Not assessed" : s.label}</span>
                      </div>
                      <p className="text-xs text-[#566270] mt-1">{s.suggestion}</p>
                      {latest && latest.level !== s.level && (
                        <p className="text-xs font-semibold text-[#2D6A4F] mt-1" data-testid={`baseline-delta-${a.key}`}>
                          Latest progress check: Level {latest.level} (was {s.level})
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
              {diag.progress_checks?.length > 0 && (
                <p className="text-xs text-[#566270] mt-3" data-testid="progress-check-count">
                  {diag.progress_checks.length} progress check{diag.progress_checks.length > 1 ? "s" : ""} completed since baseline.
                </p>
              )}
            </>
          )}
        </section>

        <section className="rounded-2xl bg-white border border-[#E5DEC9] p-6" data-testid="topic-mastery-section">
          <h2 className="font-display text-lg font-bold text-[#0B192C]">Topic mastery</h2>
          <div className="mt-3 space-y-2 max-h-80 overflow-y-auto">
            {d.mastery.length === 0 && <p className="text-sm text-[#566270]">No learning activity yet.</p>}
            {d.mastery.map((m) => (
              <div key={m.topic} className="flex items-center justify-between gap-3 text-sm">
                <div>
                  <span className="font-semibold text-[#0B192C]">{m.topic}</span>
                  <span className="text-[#566270]"> · {m.module}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-[#566270]">{m.accuracy}%</span>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${STATE_STYLE[m.state]}`}>{m.state}</span>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="rounded-2xl bg-white border border-[#E5DEC9] p-6" data-testid="module-access-section">
          <h2 className="font-display text-lg font-bold text-[#0B192C] flex items-center gap-2">
            <SlidersHorizontal className="w-4 h-4 text-[#C69B3D]" /> Module access
          </h2>
          <p className="text-xs text-[#566270] mt-1">Individual overrides take priority over group settings.</p>
          <div className="mt-3 space-y-2">
            {d.module_access.map((m) => (
              <label key={m.id} className="flex items-center justify-between gap-3 rounded-xl border border-[#F3EFE0] px-4 py-3 min-h-[52px]" data-testid={`module-toggle-${m.id}`}>
                <span className="text-sm font-semibold text-[#0B192C]">
                  {m.name}
                  {m.override !== null && m.override !== undefined && (
                    <span className="ml-2 text-[10px] font-bold uppercase text-[#7A1C2E]">override</span>
                  )}
                </span>
                <input type="checkbox" checked={m.enabled} onChange={() => toggleModule(m)}
                  className="w-6 h-6 accent-[#C69B3D]" aria-label={`Toggle ${m.name}`} />
              </label>
            ))}
          </div>
        </section>

        <section className="rounded-2xl bg-white border border-[#E5DEC9] p-6" data-testid="quests-section">
          <h2 className="font-display text-lg font-bold text-[#0B192C]">Quests</h2>
          <div className="mt-3 space-y-2">
            {d.quests.length === 0 && <p className="text-sm text-[#566270]">No Quests yet.</p>}
            {d.quests.map((qst) => (
              <div key={`${qst.title}-${qst.deadline || "none"}`} className="flex items-center justify-between text-sm">
                <span className="font-semibold text-[#0B192C]">{qst.title}</span>
                <span className="text-xs text-[#566270]">{Math.min(qst.progress, qst.target)}/{qst.target} · {qst.status}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="rounded-2xl bg-white border border-[#E5DEC9] p-6" data-testid="history-section">
          <h2 className="font-display text-lg font-bold text-[#0B192C]">Recent activity</h2>
          <div className="mt-3 space-y-2">
            {d.history.length === 0 && <p className="text-sm text-[#566270]">No completed sessions yet.</p>}
            {d.history.map((h) => (
              <div key={`${h.date}-${h.kind}-${h.topic}`} className="flex items-center justify-between text-sm">
                <span className="font-semibold text-[#0B192C] capitalize">{h.kind === "daily" ? "Daily Notes" : `${h.kind}: ${h.topic}`}</span>
                <span className="text-xs text-[#566270]">{h.score}/{h.total} · {h.date ? new Date(h.date).toLocaleDateString("en-GB") : ""}</span>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
